import type { ObjectId } from '@racemap/sdk/schema/base';
import { type UserOverview, UserOverviewSchema } from '@racemap/sdk/schema/user';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import type { DateFilter, RacemapEvent, TrashFilter } from '@racemap/utilities/types/events';
import type { Immutable } from 'immer';
import { produce } from 'immer';
import { toast } from 'src/lib/toast';
import type { StoreApi } from 'zustand';
import { type DraftState, type State, useStore } from './reducers';

const apiClient = RacemapAPIClient.fromWindowLocation();

// The list query is unpaged and spans a whole month, so it is expensive on both ends. These
// two module-level values keep repeated visits to the page from stacking copies of it up:
// the controller aborts the one a leaving user no longer needs, and the stamp lets an
// unchanged return skip the request entirely.
let inFlight: AbortController | null = null;
let lastLoad: { key: string; at: number } | null = null;
// Counts loads so a superseded one cannot clear the loading flag its replacement is still using.
let loadGeneration = 0;

/** How long an already loaded result is reused before the page asks again. */
const RELOAD_AFTER_MILLIS = 30 * OneSecondInMillis;

const loadKey = (params: LoadDataParams): string =>
  [
    params.searchBy,
    params.dateFilter.join(','),
    params.fromDate.getTime(),
    params.toDate.getTime(),
    params.userIdToSearchFor?.toHexString() ?? '',
    params.trashFilter,
  ].join('|');

const isAbort = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

export enum SearchByOptions {
  DATE = 'DATE',
  USER = 'USER',
}

export interface LoadDataParams {
  searchBy: SearchByOptions;
  dateFilter: Array<DateFilter>;
  toDate: Date;
  fromDate: Date;
  userIdToSearchFor: ObjectId | null;
  trashFilter: TrashFilter;
}

export interface ManageEventsState {
  manageEvents: {
    events: Map<string, RacemapEvent>;
    users: Map<string, UserOverview>;
    isLoading: boolean;

    actions: {
      _loadEvents: (params: LoadDataParams) => Promise<void>;
      _loadUsers: (options: { userIds: Array<ObjectId> }) => Promise<void>;
      loadData: (params: LoadDataParams) => Promise<void>;
      cancelLoad: () => void;
      deleteEvent: (eventId: string) => Promise<void>;
      deleteEvents: (eventIds: Array<string>) => Promise<void>;
    };
  };
}

export const createManageEventsStore = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Immutable<ManageEventsState> => ({
  manageEvents: {
    events: new Map(),
    users: new Map(),
    isLoading: false,
    actions: {
      _loadEvents: async ({
        searchBy,
        dateFilter,
        toDate,
        fromDate,
        userIdToSearchFor,
        trashFilter,
      }) => {
        let events: Array<RacemapEvent>;

        // A previous visit's query is of no use to anyone once these params supersede it.
        inFlight?.abort();
        const controller = new AbortController();
        inFlight = controller;
        const signal = controller.signal;

        if (searchBy === SearchByOptions.DATE) {
          if (dateFilter.length === 0) {
            set(
              produce((s: DraftState) => {
                s.manageEvents.events = new Map();
              }),
            );
            return;
          }

          events = await apiClient.getEvents(
            {
              dateFilter: [...dateFilter],
              toDate: toDate,
              fromDate: fromDate,
              show: ['hidden'],
              filter: 'can-edit',
              trashFilter,
            },
            { signal },
          );
        } else {
          if (userIdToSearchFor == null) {
            set(
              produce((s: DraftState) => {
                s.manageEvents.events = new Map();
              }),
            );
            return;
          }

          events = await apiClient.getEvents(
            {
              show: ['hidden'],
              findByCreator: userIdToSearchFor.toHexString(),
              findByEditor: userIdToSearchFor.toHexString(),
              trashFilter,
            },
            { signal },
          );
        }

        const userIdsOfEvents = new Set<string>();
        events.forEach((event) => {
          if (event.creatorId != null) {
            userIdsOfEvents.add(event.creatorId);
          }
          if (event.editorIds != null) {
            event.editorIds.forEach((editorId) => {
              userIdsOfEvents.add(editorId);
            });
          }
        });
        // hotfix: user ids become to large
        // const userIds = Array.from(userIdsOfEvents).map((id) => new ObjectId(id));
        // await get().manageEvents.actions._loadUsers({ userIds });

        set(
          produce((s: DraftState) => {
            s.manageEvents.events = new Map(events.map((e) => [e.id, e]));
          }),
        );
      },
      _loadUsers: async ({ userIds }) => {
        const { data: users } = await apiClient.getUsers({ withEventStats: true, ids: userIds });

        set(
          produce((s: DraftState) => {
            s.manageEvents.users = new Map(users.map((u) => [u.id, UserOverviewSchema.parse(u)]));
          }),
        );
      },
      loadData: async (params) => {
        const key = loadKey(params);
        const fresh =
          lastLoad != null &&
          lastLoad.key === key &&
          Date.now() - lastLoad.at < RELOAD_AFTER_MILLIS;

        // Leaving the page and coming straight back would otherwise refire the whole month.
        if (fresh && get().manageEvents.events.size > 0) return;

        const generation = ++loadGeneration;

        try {
          set(
            produce((s: DraftState) => {
              s.manageEvents.isLoading = true;
            }),
          );

          const state = get().manageEvents;
          await state.actions._loadEvents(params);
          lastLoad = { key, at: Date.now() };
        } catch (error) {
          // An aborted request was replaced or abandoned on purpose; it is not a failure, and
          // the load that replaced it owns the loading flag from here on.
          if (isAbort(error)) return;
          throw error;
        } finally {
          if (generation === loadGeneration) {
            set(
              produce((s: DraftState) => {
                s.manageEvents.isLoading = false;
              }),
            );
          }
        }
      },
      cancelLoad: () => {
        inFlight?.abort();
        inFlight = null;
      },
      deleteEvent: async (eventId) => {
        await apiClient.removeEvent(eventId);
        set(
          produce((s: DraftState) => {
            s.manageEvents.events.delete(eventId);
          }),
        );
      },
      deleteEvents: async (eventIds) => {
        try {
          const { removed } = await apiClient.removeEvents(eventIds);
          set(
            produce((s: DraftState) => {
              eventIds.forEach((id) => {
                s.manageEvents.events.delete(id);
              });
            }),
          );
          toast.success(`Removed ${removed} event${removed === 1 ? '' : 's'}.`);
        } catch (error) {
          console.log(`Failed to remove events: ${error}`);
          toast.error('Events could not be removed.');
        }
      },
    },
  },
});

export const useIsLoading = () => useStore((s) => s.manageEvents.isLoading);
export const useManageEventsActions = () => useStore((s) => s.manageEvents.actions);
